import { Injectable } from 'framework/di';

/**
 * Service for handling recommendation widget analytics and tracking data storage
 */
@Injectable
export class RecommendationAnalytic {
  /**
   * Get the storage key prefix for recommendation widgets
   * @returns Storage key prefix
   * @protected Extension point for custom storage key prefixes
   */
  protected getStorageKeyPrefix(): string {
    return 'boostSdRecommend';
  }

  /**
   * Generate a storage key for a recommendation widget
   * @param widgetId - The widget ID
   * @returns The generated storage key
   */
  generateStorageKey(widgetId: string): string {
    return `${this.getStorageKeyPrefix()}-${widgetId}`;
  }

  /**
   * Handle storage errors when storing recommendation tracking data
   * @param error - The error that occurred
   * @param data - The tracking data that failed to store
   * @protected Extension point for custom error handling
   */
  protected handleStorageError(error: Error, data: RecommendationWidgetTrackingData): void {
    console.error('Failed to store recommendation tracking data:', error, data);
  }

  /**
   * Store recommendation widget tracking data to localStorage
   * @param data - The tracking data to store
   */
  storeRecommendationWidgetTrackingData(data: RecommendationWidgetTrackingData): void {
    try {
      const storageKey = this.generateStorageKey(data.wid);
      localStorage.setItem(storageKey, JSON.stringify(data));
    } catch (error) {
      this.handleStorageError(error as Error, data);
    }
  }
}

/**
 * Tracking data for recommendation widgets
 */
export type RecommendationWidgetTrackingData = {
  /** Widget ID */
  wid: string;
  /** Recommendation ID (optional) */
  rid?: string;
  /** Recommendation Type (optional) */
  rt?: string;
};
